import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/hooks/useAuth';
import { AUTH_MESSAGES } from '../constants/authMessages';
import './LoginPage.css';

/**
 * LoginPage - 사용자 로그인 페이지
 *
 * AICC 시스템 접근을 위한 이메일/비밀번호 인증 및 이메일 기억 기능을 제공한다.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Storage key for remembered email
  const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

  // Get redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/';

  // Load remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, from]);

  /**
   * Validate form inputs
   * @returns {boolean} True if valid
   */
  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = AUTH_MESSAGES.VALIDATION_EMAIL_REQUIRED;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = AUTH_MESSAGES.VALIDATION_INVALID_EMAIL;
    }

    if (!password) {
      newErrors.password = AUTH_MESSAGES.VALIDATION_PASSWORD_REQUIRED;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await login(email, password);
      // Save or remove email based on remember checkbox
      if (rememberEmail) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      // Navigation handled by useEffect when isAuthenticated changes
    } catch (err) {
      // Error toast handled in AuthContext
      setErrors({ form: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <div className="loading-spinner"></div>
          <p>{AUTH_MESSAGES.SESSION_CHECKING}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="company-logo">
          <img src="/xsolcorp-logo.png" alt="XSolcorp Korea" />
        </div>
        <div className="login-container">
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">{AUTH_MESSAGES.LOGIN_EMAIL_LABEL}</label>
              <input
                type="email"
                id="email"
                className={`ds-input ${errors.email ? 'input-error' : ''}`}
                placeholder={AUTH_MESSAGES.LOGIN_EMAIL_PLACEHOLDER}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                autoFocus
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">{AUTH_MESSAGES.LOGIN_PASSWORD_LABEL}</label>
              <input
                type="password"
                id="password"
                className={`ds-input ${errors.password ? 'input-error' : ''}`}
                placeholder={AUTH_MESSAGES.LOGIN_PASSWORD_PLACEHOLDER}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group form-group-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  disabled={loading}
                />
                <span className="checkbox-text">{AUTH_MESSAGES.LOGIN_REMEMBER_ME}</span>
              </label>
            </div>

            {errors.form && <div className="form-error">{errors.form}</div>}

            <button
              type="submit"
              className="ds-button ds-button-primary login-button"
              disabled={loading}
            >
              {loading ? AUTH_MESSAGES.LOGIN_LOADING : AUTH_MESSAGES.LOGIN_BUTTON}
            </button>
          </form>

          <div className="login-footer">
            <p>XYRO PLUG AICC</p>
            <p className="copyright">
              &copy; {new Date().getFullYear()} XSolcorp Korea. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
